import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useDialog } from '@/composables/useDialog'

class ApiService {
  private axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v2',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Request interceptor - add auth token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const authStore = useAuthStore()
        if (authStore.token) {
          config.headers.Authorization = `Bearer ${authStore.token}`
        }
        
        // If the data is FormData, delete the Content-Type header
        // so axios can set it with the correct boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor - handle errors
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          const authStore = useAuthStore()
          authStore.logout()
          window.location.href = '/login'
        } else if (error.response?.status === 403) {
          // Forbidden / Permission Denied — detail may be a string or a structured object
          const detail = error.response?.data?.detail
          const message = typeof detail === 'object' && detail?.message
            ? detail.message
            : (typeof detail === 'string' ? detail : 'You do not have permission to perform this action.')
          const title = typeof detail === 'object' && detail?.code === 'TRIAL_EXPIRED'
            ? 'Trial Expired'
            : 'Access Denied'
          const dialog = useDialog()
          await dialog.alert(message, { title, type: 'warning' })
        }
        return Promise.reject(error)
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config)
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config)
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config)
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config)
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config)
  }
}

export const apiService = new ApiService()
